import { reaction } from 'mobx';
import { useEffect, useState } from 'react';

import { company } from '~/company/Company';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import { intercomAuth } from '~/services/IntercomAuth';
import { initIntercomKeyboardFix } from '~/services/IntercomKeyboardFix';
import {
  createIntercomOpenTracker,
  type IntercomOpenReport,
} from '~/services/IntercomOpenAttempt';
import {
  DOMAIN,
  REASON,
  reportFailure,
  reportWarning,
} from '~/services/telemetry';
import { userStore } from '~/stores';
import { INTERCOM_SCRIPT_FAILED_EVENT } from '~/utils/deferThirdParty';

interface IntercomSettings {
  app_id: string;
  name?: string;
  email?: string;
  phone?: string;
  anonymous_id?: string;
  user_id?: string | number;
  intercom_user_jwt?: string;
  [key: string]: any;
}

export type IntercomType = {
  (command: 'boot', settings: IntercomSettings): void;
  (command: 'shutdown' | 'hide' | 'show' | 'showMessages'): void;
  (command: 'update', settings?: IntercomSettings): void;
  (command: 'showNewMessage', content?: string): void;
  (...args: any[]): void;
  /** Set by the real widget once `boot` has run; absent on the SSR stub. */
  booted?: boolean;
};

declare global {
  interface Window {
    Intercom: IntercomType;
  }
}

/**
 * Where the support chat was opened from. Leaves as `from` on the BI tap
 * event and as `extras.source` on `domain:support` reports, so the values are
 * a BI-facing contract.
 */
export type SupportChatSource =
  | 'cabinet'
  | 'checkout'
  | 'floating_button'
  | 'footer'
  | 'order_page'
  | 'order_page_v3'
  | 'order_payment'
  | 'page_header'
  | 'sidebar';

interface MessengerState {
  isOpen: boolean;
  isOpening: boolean;
}

const CLOSED_MESSENGER: MessengerState = { isOpen: false, isOpening: false };

// Every useIntercom consumer used to register its own `onShow`; Intercom keeps
// a single callback per event, so only the last one could ever clear its flags.
// One registration feeds one shared state instead.
let messengerState: MessengerState = CLOSED_MESSENGER;
let openTimeoutId: ReturnType<typeof setTimeout> | undefined;

const messengerListeners = new Set<(state: MessengerState) => void>();

const OPEN_TIMEOUT_MS = 5000;

/**
 * Longer than the UI timeout on purpose: five seconds is when the launcher
 * stops spinning, not when a cold widget load on a mobile network is known to
 * have failed. An attempt stays open until this fires or `onShow` lands.
 */
const OPEN_TELEMETRY_TIMEOUT_MS = 15_000;

const setMessengerState = (patch: Partial<MessengerState>) => {
  const next = { ...messengerState, ...patch };

  if (
    next.isOpen === messengerState.isOpen &&
    next.isOpening === messengerState.isOpening
  ) {
    return;
  }

  messengerState = next;

  // Drives the status-bar backdrop strip in `src/styles/_intercom.scss`:
  // Intercom keeps the closed messenger frame mounted (hidden), so CSS alone
  // cannot tell an open messenger from a closed one.
  if (typeof document !== 'undefined') {
    document.documentElement.classList.toggle(
      'intercom-messenger-open',
      next.isOpen,
    );
  }

  messengerListeners.forEach((listener) => listener(next));
};

const stopOpening = () => {
  clearTimeout(openTimeoutId);
  openTimeoutId = undefined;
  setMessengerState({ isOpening: false });
};

/**
 * Context every `domain:support` report carries. `surface`, `company` and the
 * app version are stamped by the telemetry module itself.
 */
const supportExtras = (report?: IntercomOpenReport) => ({
  source: report?.source ?? null,
  elapsedMs: report?.elapsedMs ?? null,
  tokenObtained: report?.tokenObtained ?? null,
  isAuthorized: userStore.isAuthorized,
  scriptStatus: window.__intercomScript?.status ?? null,
  scriptLoadMs: window.__intercomScript?.loadMs ?? null,
  booted: window.Intercom?.booted ?? false,
  messengerFrame: document.querySelector('.intercom-messenger-frame') !== null,
  online: navigator.onLine,
});

const reportOpenOutcome = (report: IntercomOpenReport) => {
  if (report.outcome === 'opened') {
    analyticsService.track(AnalyticsEvent.SUPPORT_CHAT_OPENED, {
      source: report.source,
      elapsedMs: report.elapsedMs,
    });
    return;
  }
  if (report.outcome === 'script_failed') {
    stopOpening();
    reportFailure(DOMAIN.support, 'Intercom widget script failed to load', {
      reason: REASON.support.widgetScriptFailed,
      once: true,
      extras: supportExtras(report),
    });
    return;
  }
  reportFailure(DOMAIN.support, 'Intercom messenger did not open in time', {
    reason: REASON.support.openTimeout,
    once: true,
    extras: supportExtras(report),
  });
};

const openTracker = createIntercomOpenTracker(
  OPEN_TELEMETRY_TIMEOUT_MS,
  reportOpenOutcome,
);

const reportIdentityTokenMissing = (
  context: 'boot' | 'open',
  source?: SupportChatSource,
) =>
  reportWarning(DOMAIN.support, 'Intercom identity token unavailable', {
    reason: REASON.support.identityTokenFailed,
    once: true,
    extras: { ...supportExtras(), context, source: source ?? null },
  });

const resetMessengerState = () => {
  clearTimeout(openTimeoutId);
  openTimeoutId = undefined;
  openTracker.cancel();
  setMessengerState(CLOSED_MESSENGER);
};

// The widget may never show — blocked by an ad blocker, offline, CSP. Without
// this the launcher would sit in its loading state until a page reload.
const startOpening = () => {
  clearTimeout(openTimeoutId);
  openTimeoutId = setTimeout(stopOpening, OPEN_TIMEOUT_MS);
  setMessengerState({ isOpening: true });
};

// `boot` and `shutdown` each replace the messenger instance and drop the
// handlers registered against the previous one, so this runs after both.
// Intercom keeps a single callback per event, so re-registering never stacks.
const registerMessengerHandlers = () => {
  if (!window.Intercom) return;

  window.Intercom('onShow', () => {
    clearTimeout(openTimeoutId);
    openTimeoutId = undefined;
    setMessengerState({ isOpen: true, isOpening: false });
    openTracker.opened();
  });
  window.Intercom('onHide', () => {
    clearTimeout(openTimeoutId);
    openTimeoutId = undefined;
    setMessengerState({ isOpen: false, isOpening: false });
    openTracker.cancel();
  });
};

const bootIntercomWithUserData = async () => {
  if (!company.isEnabledIntercom) return;
  if (!window.Intercom) return;

  const token = await intercomAuth.getFreshToken();
  if (!token) {
    reportIdentityTokenMissing('boot');
    return;
  }

  window.Intercom('boot', {
    app_id: company.envVariables.REACT_APP_INTERCOM_ID,
    user_id: userStore.personalData.id,
    intercom_user_jwt: token,
    name: userStore.fullName || undefined,
    company: {
      company_id: company.config.id,
      name: company.config.name,
    },
  });

  registerMessengerHandlers();
};

if (typeof window !== 'undefined') {
  if (company.isEnabledIntercom) {
    initIntercomKeyboardFix();
    document.addEventListener(INTERCOM_SCRIPT_FAILED_EVENT, () => {
      openTracker.scriptFailed();
    });
  }

  reaction(
    () => userStore.isAuthorized,
    (isAuthorized) => {
      if (!company.isEnabledIntercom) return;

      if (isAuthorized) {
        bootIntercomWithUserData();
      } else {
        intercomAuth.invalidate();
        if (window.Intercom) {
          window.Intercom('shutdown');
          resetMessengerState();
          registerMessengerHandlers();
        }
      }
    },
    { fireImmediately: true },
  );
}

export const useIntercom = () => {
  const [{ isOpening, isOpen }, setState] =
    useState<MessengerState>(messengerState);

  /**
   * Opens the messenger from `source`, tracking the tap on the way. `payload`
   * extends the tap event (an order id on the order page).
   */
  const openMessenger = async (
    source: SupportChatSource,
    payload: Record<string, unknown> = {},
  ) => {
    analyticsService.track(AnalyticsEvent.CONTACT_SUPPORT_BUTTON_CLICKED, {
      ...payload,
      from: source,
    });

    if (!company.isEnabledIntercom) return;
    if (!window.Intercom) {
      reportFailure(DOMAIN.support, 'Intercom stub missing', {
        reason: REASON.support.stubMissing,
        once: true,
        extras: { ...supportExtras(), source },
      });
      return;
    }

    // Already open: Intercom will not fire `onShow` again, so flagging the
    // launcher as opening would never be undone.
    if (messengerState.isOpen) {
      window.Intercom('showMessages');
      return;
    }

    startOpening();
    openTracker.start(source);

    // Load the heavy widget bundle on demand (first open). Calls made before it
    // finishes are queued by the Intercom stub, so nothing is lost.
    window.__loadIntercom?.();

    if (userStore.isAuthorized) {
      const token = await intercomAuth.getFreshToken();
      openTracker.tokenResolved(Boolean(token));
      if (!token) {
        reportIdentityTokenMissing('open', source);
      }
      window.Intercom('showMessages');
      window.Intercom('update', {
        name: userStore.fullName,
        ...(token ? { intercom_user_jwt: token } : {}),
      });
    } else {
      // Anonymous visitors are never booted by the auth reaction (it only boots
      // authorized users), so the messenger had no session to open. Boot an
      // anonymous session first, then show it.
      window.Intercom('boot', {
        app_id: company.envVariables.REACT_APP_INTERCOM_ID,
      });
      registerMessengerHandlers();
      window.Intercom('showMessages');
    }
  };

  const updateIntercom = async (settings: Omit<IntercomSettings, 'app_id'>) => {
    if (!company.isEnabledIntercom) return;
    if (!window.Intercom) return;

    const token = userStore.isAuthorized
      ? await intercomAuth.getFreshToken()
      : null;

    window.Intercom('update', {
      ...(token ? { intercom_user_jwt: token } : {}),
      ...settings,
    });
  };

  const shutdownIntercom = () => {
    if (!company.isEnabledIntercom) return;
    if (window.Intercom) {
      window.Intercom('shutdown');
      resetMessengerState();
      registerMessengerHandlers();
    }
  };

  useEffect(() => {
    if (!company.isEnabledIntercom) return;

    registerMessengerHandlers();

    setState(messengerState);
    messengerListeners.add(setState);

    return () => {
      messengerListeners.delete(setState);
    };
  }, []);

  return {
    openMessenger,
    updateIntercom,
    shutdownIntercom,
    isOpening,
    isOpen,
  };
};
